import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {ObjectTypes, ApiErrorCodes, SortDirections} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import {useGenericResultsDataApi} from '../../../api/hooks';
import {useSMCSnackbar, useColumnsWidthsPersistedState, useSort, useColumnsStatePersistedState} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import {AdministratorGetListSchema, AdministratorStatuses, AdministratorRoles} from '../../../api/DBSchema/Administrator';
import store from '../../../store';
import connectedLogo from '../../../assets/images/gateway/connected.png';
import disconnectedLogo from '../../../assets/images/gateway/disconnected.png';
import DescriptionCell from '../../../components/core/SMCTable/CommonCells/DescriptionCell';
import EditObjectCell from '../../../components/core/SMCTable/CommonCells/EditObjectCell';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {buildStringSortCaseInsensitive} from '../../../utils/sortUtils';

const RoleCell = React.memo(({value}) => {
    const { t } = useTranslation();

    switch (value) {
        case AdministratorRoles.administrator:
            return <span>{t('SMC_ADMIN_ROLE_Administrator')}</span>;
        case AdministratorRoles.readOnly:
            return <span>{t('SMC_ADMIN_ROLE_ReadOnly')}</span>;
        case AdministratorRoles.multiDomain:
            return <span>{t('SMC_ADMIN_ROLE_MultiDomain')}</span>;
        case AdministratorRoles.multiDomainReadOnly:
            return <span>{t('SMC_ADMIN_ROLE_MultiDomainReadOnly')}</span>;
        default:
            return undefined;
    }
});

const StatusCell = React.memo(({value}) => {
    const { t } = useTranslation();

    if (value === AdministratorStatuses.connected) {
        return <img src={connectedLogo} title={t('SMC_USER_LOGGED_IN')} alt="logged in" />;
    } else {
        return <img src={disconnectedLogo} title={t('SMC_USER_NOT_LOGGED_IN')} alt="not logged in" />;
    }
});

export function useAdminstratorResults() {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    const { sortBasis, sortDirection, setSort } = useSort('userId', SortDirections.ASCENDING);

    const columns = useMemo(() => ([
        { id: 'userId',      accessor: AdministratorGetListSchema.userId,      header: t('SMP.ADMINISTRATOR.USER_ID'),                                width: 130,                 defaultShow: true, sort: buildStringSortCaseInsensitive(AdministratorGetListSchema.userId),      Cell: EditObjectCell  },
        { id: 'description', accessor: AdministratorGetListSchema.description, header: '',                                name: t('SMP.DESCRIPTION'), width: 32,                  defaultShow: true, sort: buildStringSortCaseInsensitive(AdministratorGetListSchema.description), Cell: DescriptionCell },
        { id: 'firstName',   accessor: AdministratorGetListSchema.firstName,   header: t('SMP.ADMINISTRATOR.FIRST_NAME'),                             width: 115,                 defaultShow: true, sort: buildStringSortCaseInsensitive(AdministratorGetListSchema.firstName),                         },
        { id: 'lastName',    accessor: AdministratorGetListSchema.lastName,    header: t('SMP.ADMINISTRATOR.LAST_NAME'),                              width: 115,                 defaultShow: true, sort: buildStringSortCaseInsensitive(AdministratorGetListSchema.lastName),                          },
        { id: 'email',       accessor: AdministratorGetListSchema.email,       header: t('SMP.ADMINISTRATOR.EMAIL'),                                  width: 215,                 defaultShow: true, sort: buildStringSortCaseInsensitive(AdministratorGetListSchema.email),                             },
        { id: 'phone',       accessor: AdministratorGetListSchema.phone,       header: t('SMP.ADMINISTRATOR.PHONE'),                                  width: 115,                 defaultShow: true, sort: buildStringSortCaseInsensitive(AdministratorGetListSchema.phone),                             },
        { id: 'role',        accessor: AdministratorGetListSchema.role,        header: t('SMP.ADMINISTRATOR.ROLE'),                                   width: 185,                 defaultShow: true, sort: buildStringSortCaseInsensitive(AdministratorGetListSchema.role),        Cell: RoleCell        },
        { id: 'status',      accessor: AdministratorGetListSchema.status,      header: t('SMP.ADMINISTRATOR.STATUS'),                                 width: 55, align: 'center', defaultShow: true, sort: buildStringSortCaseInsensitive(AdministratorGetListSchema.status),      Cell: StatusCell      }
    ]), [t]);

    const rowIdSelector = (obj) => obj[AdministratorGetListSchema.userId];
    
    const defaultColumnsState = useMemo(() => (
        columns.map(column => ({
            id: column.id,
            show: Boolean(column.defaultShow)
        }))
    ), [columns]);
    const [columnsState, setColumnsState] = useColumnsStatePersistedState(ObjectTypes.ADMINISTRATOR, defaultColumnsState);
    const setDefaultColumnsState = useCallback(() => setColumnsState(defaultColumnsState), [defaultColumnsState, setColumnsState]);

    const [columnsWidths, setColumnsWidths] = useColumnsWidthsPersistedState(ObjectTypes.ADMINISTRATOR, null);

    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const isFirstRender = useRef(true);

    const {
        tableContent,
        isFetching,
        fetchNextPage,
        isLastPage,
        reloadTable,
        reloadTableNoClearSelected,
        filterObject,
        setFilterObject,
        freeTextSearch,
        selectedObjects,
        setSelectedObjects,
        deleteSelectedObjects,
    } = useGenericResultsDataApi(ObjectTypes.ADMINISTRATOR);

    const newObjectCallback = useCallback((objectId, errorCode) => {
        if (errorCode) {
            alert(t(errorCode));
            // enqueueErrorSnackbar(t(errorCode));
        } else if (objectId) {
            store.dispatch(navigateTo({search: 'id=' + objectId}));
        }
    }, [t]);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        if (window.confirm(t('RESULTS_OBJECTS_DELETE_QUES'))) {
            setIsPageLoaded(false);
            deleteSelectedObjects()
                    .then(({successMessage, errorMessage}) => {
                        successMessage && enqueueSuccessSnackbar(successMessage);
                        errorMessage && enqueueErrorSnackbar(errorMessage);
                        reloadTable();
                    })
                    .catch(e => {
                        const errorMessage = getErrorMessage(e);
                        enqueueErrorSnackbar(errorMessage);
                    })
                    .finally(() => {
                        setIsPageLoaded(true);
                    });
        }
    }, [selectedObjects, deleteSelectedObjects, reloadTable, enqueueSuccessSnackbar, enqueueErrorSnackbar, getErrorMessage, t]);

    // Reload table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTable,  enqueueErrorSnackbar, t]);

    // Reload table without clearing selected on filter changed
    useEffect(() => {
        // Don't run on first render, only when filter is changed
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        reloadTableNoClearSelected()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTableNoClearSelected, filterObject, enqueueErrorSnackbar, t]);

    return ({
        tableContent,
        isFetching,
        isPageLoaded,
        filterObject,
        setFilterObject,
        freeTextSearch,
        fetchNextPage,
        isLastPage,
        reloadTable,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        columnsWidths,
        setColumnsWidths,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        newObjectCallback,
        reloadButtonOnClick,
        deleteButtonOnClick
    });
}